import AppConfig, { AutofilingApiConfig } from '@app/types/AppConfig';
import { ServiceType } from '@app/types/ServiceType';
import { isMappedProductionHost, normalizeLifecycleEnv, resolveRegionFromHost } from '@app/utils/telemetry';

const w = window as any;
const hostName = typeof window !== 'undefined' ? window.location.hostname : '';
const configuredEnv =
  process.env.REACT_APP_KUBE_NAMESPACE || (w._env_ && w._env_.REACT_APP_KUBE_NAMESPACE) || process.env.REACT_APP_BACKEND_ENV || (w._env_ && w._env_.REACT_APP_BACKEND_ENV) || 'dev';
const lifecycleEnv = isMappedProductionHost(hostName) ? 'prod' : normalizeLifecycleEnv(configuredEnv);
const region = resolveRegionFromHost(hostName);

const config: AppConfig = {
  logLevel: process.env.REACT_APP_LOG_LEVEL || (w._env_ && w._env_.REACT_APP_LOG_LEVEL),
  otpFlowBaseURL: process.env.REACT_APP_OTP_FLOW_BASEURL || (w._env_ && w._env_.REACT_APP_OTP_FLOW_BASEURL),
  api: {
    [ServiceType.Portal]: {
      getSubmittedFilesColumns: `api/files/submitted/columns`,
      getSubmittedFiles: `api/files/submitted`,
      portalFilesUpload: `api/files`,
      downloadPortalZipFile: `api/files/submitted/zip`,
      downloadPortalFile: `api/files/submitted/{id}`,
      siteSearch: `api/sites`,
      getPendingFiles: `api/file-aggregations/user-pending-files`,
      getPendingFilesCard: `api/file-aggregations/pending-files-summary`,
      getFullfilledFiles: `api/file-aggregations/fulfilled-files`,
      portalFileActions: `api/files`,
      portalFileActionsReject: `api/files/reject`,
      portalFileActionsAccept: `api/files/accept`,
      portalFileRequests: `api/file-requests`,
      portalFileRequestById: `api/ext/requests/{id}`,
      validatePortalFileRequest: `api/ext/requests/{requestId}/token`,
      submitPortalFiles: `api/file-requests/{id}/file`,
      downloadPortalFiles: `api/files/{id}`,
      downloadPortalZipFiles: `api/files/zip`,
    },
    [ServiceType.FileStorage]: {
      uploadFiles: `api/files`,
      copyFiles: `api/files/copy`,
      createSingleDocument: `api/files/clone-from-template`,
    },
    organizationAPI: {
      verticals: `api/verticals`,
      integrations: `api/organizations/integrations`,
      supportLevels: `api/licenses/supportlevels`,
      compliances: `api/licenses/compliances`,
      licenses: `api/licenses`,
      licenseStatuses: `api/licenses/statuses`,
      menus: `api/menus`,
      organizations: `api/organizations`,
      storages: `api/licenses/storages`,
      userCounts: `api/licenses/usercounts`,
      functionalFlow: `api/functionalflows`,
      functionalFlowByLicence: `api/organizations/{id}/flows`,
      updateFunctionalFlows: `api/organizations/{id}/flows`,
      getEnabledFlows: `api/organizations/{id}/modules/flows`,
      countries: `api/organizations/countries`,
      statusesByLicense: `api/licenses/{id}/statuses`,
    },
    [ServiceType.FileManagement]: {
      getPublishedFilesColumns: `api/files/published-files/columns`,
      getPublishedFiles: `api/files/published-files`,
      getPublishedFilesCount: `api/files/published-files?count=true`,
      downloadFile: `api/files/{id}`,
      downloadZipFile: `api/files/zip`,
      downloadPublishedFile: `api/files/published-files/{id}`,
      downloadPublishedZipFile: `api/files/published-files/zip`,
      deleteFilesByIds: `api/files`,
      fileDetailsByFile: `api/files/{id}/details`,
      files: `api/files`,
      fileAreasByChannel: `api/fileAreas`,
      fileAreasByChannelList: `api/FileAreas/fileArea-list`,
      fileStatuses: `api/tenants/fileStatuses`,
      fileStatus: `api/files/status`,
      years: `api/tenants/years`,
      projectsBySite: `api/fileareas/{id}/projects`,
      usersBySite: `api/fileareas/{id}/users`,
      saveFileDetails: `api/files/details`,
      saveAcceptFileDetails: `api/files/accept/details`,
      folderStructureBySite: `api/fileAreas/{id}/folders`,
      tags: `api/binders/{id}/tags`,
      allTags: 'api/Tags',
      fileAreaSettingsBySite: `api/fileAreas/{id}/settings`,
      checkout: `api/files/{id}/checkout`,
      undoCheckout: `api/files/{id}/undo`,
      checkedOutFiles: `api/files/checkouts`,
      checkedOutFilesHistory: `api/files/{id}/checkouts`,
      assignmentFilesHistory: `api/files/{id}/assignments`,
      updateAssigneeNStatus: `api/files/assignee`,
      checkiIn: `api/files/checkin`,
      createFilterTemplate: `api/fileAreas/{id}/templates`,
      modifyFilterTemplate: `api/fileAreas/{siteId}/templates/{templateId}`,
      deleteFilterTemplate: `api/fileAreas/{siteId}/templates/{templateId}`,
      getTemplates: `api/fileAreas/{id}/templates`,
      portalFileRequestWithKey: `api/file-requests/{id}`,
      recategorizeFiles: `api/files/fileArea/{id}`,
      updateFileRequest: `api/file-requests/{id}`,
      publishFiles: `api/files/published-files`,
      getFileHistory: `api/files/file-eventhistory`,
      lastAccessedSites: `api/files/last-accessedsites`,
      lastAccessedFiles: `api/files/last-accessedfiles`,
      retentions: `api/retentions`,
      retentionsSoftDelete: `api/retentions/SoftDelete`,
      retentionsCSV: `api/Retentions/reports/csv`,
      recategorize: `api/files/recategorize`,
      moveFiles: `api/files/move`,
      reNameFiles: `api/files/rename`,
      pin: `api/files/pin`,
      canCheckinByFileId: `api/files/{id}/can-checkin`,
      linked: `api/files/linked`,
      links: `api/files/links`,
      unlinked: `api/files/unlinked`,
      fileAreaIdBySite: `api/fileAreas/{id}/details`,
      binderTemplates: `api/BinderTemplates`,
      binderTemplateFolders: `api/BinderTemplates/{BinderId}/nodes`,
      clientFileAreasList: `api/Binders`,
      updateBinderTemplates: 'api/BinderTemplates/{templateId}',
      updateFileArea: `api/Binders/{id}`,
      binderFileAreaNodes: `api/Binders/{id}/nodes`,
      createFileArea: `api/Binders`,
      templateReused: `api/Binders/template-reused`,
      fileAreaAvailabilityBySite: `api/Binders/{siteId}`,
      clients: 'api/fileAreas/fileArea-list',
      autoFilingRules: `api/AutoFilingRules`,
      parseFilename: `api/AutoFilingRules/parse-filename`,
      getAvailableJobs: `api/tenants/available-jobs`,
      verifySecureLink: `api/SecureLinks/{secureLinkReference}/verify`,
      isSendOnBehalfEnabled: `api/files/email-config`,
      getSecureLinkDetails: `api/SecureLinks`,
      cancelSecureLink: `api/SecureLinks`,
      manualSearch: `api/files/manual-search`,
      manualSearchAsk: `api/files/manual-search-ask`,
      fileSearch: 'api/file-search',
      fileSearchColumns: 'api/file-search/columns',
      searchCSV: `api/file-search/reports/csv`,
      ocrSearch: 'api/file-search/advanced',
      foldersByTemplateId: `api/BinderTemplates/{templateId}/nodes`,
      clientTypes: `api/ClientDetails/client-types`,
      retentionSearchAsk: `api/Retentions/search-ask`,
      retentionSearchCondtions: `api/Retentions/search-conditions`,
      fileVersions: `api/FileVersions/{fileId}`,
      downloadFileVersion: `api/FileVersions/{fileId}/{versionNumber}/download`,
      createNewFileVersion: `api/FileVersions/{fileId}/create-version`,
      restoreFileVersion: `api/FileVersions/{fileId}/{versionNumber}/restore`,
      // Bulk Document Creation — Step 1 client selection (verify service/prefix against backend).
      bulkDocClientFileAreaTemplates: `api/v1/BulkDocuments/client-file-area-templates`,
      bulkDocAllMatchingClientKeys: `api/v1/BulkDocuments/client-file-area-templates/all-matching-keys`,
      bulkDocExcludedClients: `api/v1/BulkDocuments/excluded-clients`,
      bulkDocFileAreaTemplates: `api/v1/BulkDocuments/file-area-templates`,
      bulkDocRuns: `api/v1/BulkDocuments/runs`,
      bulkDocRunsBase: `api/v1/BulkDocuments`,
      bulkDocJobDetail: `api/v1/BulkDocuments/runs/jobdetail`,
      binderTemplatesListNodes: `api/BinderTemplates/list/nodes`,
      /** Manual Routing queue/folder list (File Management). */
      manualRoutingQueues: `api/v1/source-folders/manual-routing-queues`,
    },
    [ServiceType.UserManagement]: {
      userPermissions: `api/permissions/logged-user-permission`,
      userNavigationMenu: `api/users/internal-menu`,
      userPreference: `api/users/logged-user-preference`,
      crossOrganizationUserExsistance: `api/users`,
      createUser: `api/users`,
      userById: `api/users/{id}`,
      getUserRoles: `api/users/roles`,
      getUserStatuses: `api/users/statuses`,
      getUserCount: `api/users/count`,
      requestResetPassword: `api/users/password`,
      getUserGroups: `api/groups`,
      usersByGroupId: `api/groups/{id}/users/user-list`,
      getModulePermission: `api/permissions`,
      createUserGroup: `api/groups`,
      userGroupByuser: `api/users/{id}/groups/group-list`,
      getUserGroup: `api/groups/group-list`,
      getUserList: `api/users/user-list`,
      userGroupById: `api/groups/{id}`,
      modulePermissionGroupById: `api/groups/{id}/permissions`,
    },
    [ServiceType.MasterData]: {
      channels: `api/channels/channel-list`,
      createChannel: `api/channels`,
      channelById: `api/channels/{id}`,
      checkChannelExsistance: `api/channels`,
      getFolders: `api`,
      getSitesByChannel: `api/channels/{id}/sites`,
      createContact: `api/contacts`,
      createSite: `api/channels/{channelId}/sites`,
      checkSiteByChannel: `api/channels/{channelId}/sites`,
      updateSite: `api/channels/{channelId}/sites/{siteId}`,
      clientList: `api/contacts/contact-list`,
      sites: `api/sites`,
      siteById: `api/sites/{id}`,
      contactsBysiteId: `api/sites/{siteId}/contacts`,
      crmSiteIdAvailable: 'api/Sites/crmsite',
      siteNameAvaialble: 'api/channels/site',
      templates: 'api/Templates',
      updateTemplates: 'api/Templates/{templateId}',
      foldersBySiteId: `api/sites/{siteId}/folders`,

      jobTypes: `api/jobtypes`,
      getJobs: `api/Jobs`,
      isMultipleBindersAllowed: `api/BinderSettings`,
      replaceTerms: `api/ReplaceTerms`,
      contactsByClientId: `api/sites/{clientId}/contacts/search`,
    },
    [ServiceType.Dashboard]: {
      getWidgetPermissions: `api/dashboards/{id}/widgets`,
      firmLogoAndName: `api/Dashboards/firmDetails`,
    },
    [ServiceType.Bff]: {
      filter: `api/filters`,
      filterById: `api/filters/{id}`,
      filterDefault: `api/filters/{id}/default`,
      autoFilingLogs: `api/AutoFilingLogs`,
      emailNotification: `api/FileNotifications/email`,
      /** Manual Routing documents on webbff (webdata) — queues live on File Management. */
      manualRoutingDocumentsSearch: `api/v1/manual-routing/documents/search`,
      manualRoutingDocumentDownload: `api/v1/manual-routing/documents`,
      manualRoutingDocumentsColumns: `api/v1/manual-routing/documents/columns`,
    },
    [ServiceType.Notification]: {
      pushNotification: `api/notificationHub`,
    },
    [ServiceType.DocumentAdministration]: {
      getDefaultTaxYear: `api/FileConfigurations`,
      getFeatures: `api/Features`,
      getVirusScanStatus: `api/VirusScan/Status`,
      fileConfiguration: `api/FileConfigurations`,
      fileTemplatesList: `api/FileTemplates/list`,
    },
    [ServiceType.Autofiling]: {
      autoFilingManualRoute: `api/AutoFiling/manual-route`,
    } as AutofilingApiConfig,
    [ServiceType.AgentApi]: {
      chat: `api/v1/agent/chat`,
    },
    [ServiceType.PortalAdmin]: {
      portalFoldersByClientId: `api/ClientDetails/clients/{clientId}/portal/published-folder-structure`,
    },
  },
  inputDebounceInterval: 500,
  dateFormat: 'YYYY-MM-DD',
  idleTime: 6000000,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID || (w._env_ && w._env_.REACT_APP_OKTA_CLIENT_ID),
  telemetryConnectionString: process.env.REACT_APP_TELEMETRY_CONNECTIONSTRING || (w._env_ && w._env_.REACT_APP_TELEMETRY_CONNECTIONSTRING),
  telemetry: {
    lifecycleEnv,
    region,
  },
};

const changeApi: AppConfig = ((): AppConfig => {
  Object.keys(config.api).forEach((e) => {
    Object.keys((config.api as any)[e]).forEach((v) => {
      (config.api as any)[e][v] = e + '/' + (config.api as any)[e][v];
    });
  });
  return config;
})();

export default changeApi;
